import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import { useIdleTimer } from 'react-idle-timer';
import ProtectedRoute from 'components/protected-route/ProtectedRoute';

import { SCENARIO_LIST } from 'constants/components';
import {
    LOGIN_ROUTE,
    BASE_ROUTE,
    MARKET_ROUTE,
    RM_ROUTE,
    SCENARIO_FOUR_ROUTE,
    SCENARIO_ONE_ROUTE,
    SCENARIO_THREE_ROUTE,
    SCENARIO_TWO_ROUTE,
    TEAM_ROUTE
} from 'constants/routes';

import Landing from 'views/Landing/Landing';
import Market from 'views/Market/Market';
import Rm from 'views/Rm/Rm';
import ScenarioFour from 'views/ScenarioFour/ScenarioFour';
import ScenarioOne from 'views/ScenarioOne/ScenarioOne';
import ScenarioThree from 'views/ScenarioThree/ScenarioThree';
import ScenarioTwo from 'views/ScenarioTwo/ScenarioTwo';
import Team from 'views/Team/Team';
import LoginView from 'views/Login/LoginView';

import Header from 'components/header/Header';
import RmInvestigationOverlay from 'components/rm-investigation-overlay/RmInvestigationOverlay';
import OverallAppOverlay from './components/overall-app-overlay/OverallAppOverlay';
import Footer from 'components/footer/Footer';

import {
    fetchLastRefreshDate,
    hideUserProfileOverlay,
    logout
} from 'slices/AppSlice';
import { selectLastRefreshDate } from 'selectors/selectors';

function App() {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();

    const SESSION_TIMEOUT = 1000 * 60 * 15;

    const onIdle = () => {
        dispatch(hideUserProfileOverlay());
        dispatch(logout());
        navigate(LOGIN_ROUTE);
    };

    useIdleTimer({
        onIdle,
        timeout: SESSION_TIMEOUT,
        eventsThrottle: 500
    });

    useEffect(() => {
        dispatch(fetchLastRefreshDate());
    }, []);

    const lastRefreshDate = useSelector(selectLastRefreshDate);
    const scenarioOneRmScatterChartRef = useRef(null);

    const [navigateHistory, setNavigateHistory] = useState(['/']);
    const [navMenuItemHovered, setNavMenuItemHovered] = useState(null);
    const navigateTo = (from, to) => {
        if (
            [
                SCENARIO_FOUR_ROUTE,
                SCENARIO_ONE_ROUTE,
                SCENARIO_THREE_ROUTE,
                SCENARIO_TWO_ROUTE
            ].includes(to)
        ) {
            const scenario = SCENARIO_LIST.find((s) => s.path === to);
            setNavMenuItemHovered(scenario.id - 1);
        } else {
            setNavMenuItemHovered(null);
        }

        if (from === to) {
            return;
        }

        if (to === '/') {
            setNavigateHistory(['/']);
            return navigate(to);
        }

        if (navigateHistory.includes(to)) {
            const index = navigateHistory.findIndex((x) => x === to);
            setNavigateHistory(navigateHistory.slice(0, index));
            return navigate(to);
        }

        if (navigateHistory.includes(from)) {
            return navigate(to);
        }

        navigateHistory.push(from);
        setNavigateHistory(navigateHistory);
        return navigate(to);
    };

    // handle back button
    window.onpopstate = () => {
        navigateTo(
            navigateHistory[navigateHistory.length - 2],
            navigateHistory[navigateHistory.length - 1]
        );
    };

    if (location && location.pathname.includes(LOGIN_ROUTE)) {
        return <LoginView />;
    }

    return (
        <div className="main-wrapper">
            <OverallAppOverlay />
            <RmInvestigationOverlay />
            <Header
                ref={scenarioOneRmScatterChartRef}
                navigateHistory={navigateHistory}
                navigateTo={navigateTo}
                navMenuItemHovered={navMenuItemHovered}
                setNavMenuItemHovered={setNavMenuItemHovered}
            />

            <Routes>
                <Route
                    path={BASE_ROUTE}
                    element={
                        <ProtectedRoute>
                            <Landing navigateTo={navigateTo} />
                        </ProtectedRoute>
                    }
                    exact
                />
                <Route
                    path={MARKET_ROUTE}
                    element={
                        <ProtectedRoute>
                            <Market navigateTo={navigateTo} />
                        </ProtectedRoute>
                    }
                    exact
                />
                <Route
                    path={TEAM_ROUTE}
                    element={
                        <ProtectedRoute>
                            <Team navigateTo={navigateTo} />
                        </ProtectedRoute>
                    }
                    exact
                />
                <Route
                    path={RM_ROUTE}
                    element={
                        <ProtectedRoute>
                            <Rm navigateTo={navigateTo} />
                        </ProtectedRoute>
                    }
                    exact
                />
                <Route
                    path={SCENARIO_ONE_ROUTE}
                    element={
                        <ProtectedRoute>
                            <ScenarioOne ref={scenarioOneRmScatterChartRef} />
                        </ProtectedRoute>
                    }
                    exact
                />
                <Route
                    path={SCENARIO_TWO_ROUTE}
                    element={
                        <ProtectedRoute>
                            <ScenarioTwo />
                        </ProtectedRoute>
                    }
                    exact
                />
                <Route
                    path={SCENARIO_THREE_ROUTE}
                    element={
                        <ProtectedRoute>
                            <ScenarioThree />
                        </ProtectedRoute>
                    }
                    exact
                />
                <Route
                    path={SCENARIO_FOUR_ROUTE}
                    element={
                        <ProtectedRoute>
                            <ScenarioFour />
                        </ProtectedRoute>
                    }
                    exact
                />
            </Routes>
            <Footer lastRefreshDate={lastRefreshDate} />
        </div>
    );
}

export default App;
